import { ref, watch } from 'vue';

const STORAGE_KEY = 'lagomorpha.preferences';
const isBrowser = typeof window !== 'undefined';

export const PreferenceKey = {
    NavDrawerPinned: 'navDrawerPinned',
    RecipesViewMode: 'recipesViewMode',
    CalendarFilters: 'calendarFilters',
    CalendarView: 'calendarView',
    BakeModeRoundBatchesUp: 'bakeModeRoundBatchesUp',
    BakeModeShowConnectors: 'bakeModeShowConnectors',
    BakeModeKeepScreenOn: 'bakeModeKeepScreenOn',
} as const;

type PreferenceKeyValue = (typeof PreferenceKey)[keyof typeof PreferenceKey];
type PreferenceValue = string | number | boolean | object | Array<unknown> | null;
type PreferenceStore = Record<string, PreferenceValue>;

const readStore = (): PreferenceStore => {
    if (!isBrowser) {
        return {};
    }

    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) {
        return {};
    }

    try {
        const parsed = JSON.parse(raw);
        return typeof parsed === 'object' && parsed !== null ? parsed : {};
    } catch {
        return {};
    }
};

const writeStore = (store: PreferenceStore) => {
    if (!isBrowser) {
        return;
    }

    try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
    } catch {
        // storage might be unavailable (e.g. private mode); ignore silently
    }
};

export const readPreference = <T extends PreferenceValue>(key: PreferenceKeyValue | string, defaultValue: T): T => {
    const store = readStore();
    return (store[key] as T | undefined) ?? defaultValue;
};

export const writePreference = <T extends PreferenceValue>(key: PreferenceKeyValue | string, value: T) => {
    const store = readStore();
    store[key] = value;
    writeStore(store);
};

export const usePreference = <T extends PreferenceValue>(key: PreferenceKeyValue | string, defaultValue: T) => {
    const state = ref<T>(readPreference(key, defaultValue));

    watch(state, (value) => {
        writePreference(key, value);
    });

    return state;
};
